import { defineComponent as _defineComponent } from 'vue'
import { computed, toRefs, useAsync, useRoute, useStore, watch } from "@nuxtjs/composition-api";

import {
  useAmplitude,
  useBranchService,
  useLike,
  useLtk,
  useProduct,
  useProductImageProps,
  useProductLinkProps,
  useProductPricing,
} from "@/composables";
import { useConsumerSearchStore } from "@/store";
import { getAmplitudeStore } from "@/store/amplitude";
import { useCreatorShop } from "@/store/creator-shop";
import { getExperimentsStore } from "@/store/experiments";
import { getProductDetailsStore } from "@/store/product-details";
import { getProductsStore } from "@/store/products";
import { getSearchStore } from "@/store/search";
import { EXPERIMENTS_CONSUMER_SEARCH, FEATURE_FLAG_ACTIVE, FLAG_PRICE_DROP_BADGE } from "@/utils/experiments/constants";
import { Actions } from "@/utils/snowplow/enums";
import { createProductImpression } from "@/utils/snowplow/factory";


export default /*#__PURE__*/_defineComponent({
  __name: 'LtkProduct',
  props: {
  productId: {
    type: String,
    required: true,
  },
  discountPercent: {
    type: Number,
    default: 0,
  },
  showDiscount: {
    type: Boolean,
    default: false,
  },
  showDiscountBadge: {
    type: Boolean,
    default: false,
  },
  showProductDetails: {
    type: Boolean,
    default: false,
  },
  showRetailer: {
    type: Boolean,
    default: false,
  },
  showShopNow: {
    type: Boolean,
    default: false,
  },
  shareButton: {
    type: Boolean,
    default: false,
  },
  likeButtonOffset: {
    type: String,
    default: () => "14px",
  },
  likeButtonSize: {
    type: String,
    default: () => "32px",
  },
  referringComponent: {
    type: String,
    default: "",
  },
},
  setup(__props) {

const props = __props;



const {
  productId,
  discountPercent,
  showDiscount,
  showProductDetails,
  showRetailer,
  showShopNow,
  shareButton,
  referringComponent,
} = toRefs(props);

const amplitude = useAmplitude();
const route = useRoute();
const store = useStore();
const productsStore = getProductsStore(store);
const productDetailsStore = getProductDetailsStore(store);
const branch = useBranchService();
const searchStore = getSearchStore(store);
const experimentsStore = getExperimentsStore(store);
const amplitudeStore = getAmplitudeStore(store);
const consumerSearchStore = useConsumerSearchStore();

const fetch = async () => {
  // Fetch ltk product if it has not yet been loaded
  await productsStore.fetchProductById(productId.value);
  const productDetailsId = productsStore.products[productId.value]?.productDetailsId;
  if (productDetailsId) {
    await productDetailsStore.fetchProductDetailById(productDetailsId);
  }
  return true;
};

useAsync(fetch, `ltk-product:${productId.value}`);

watch(productId, fetch, { immediate: false });

const { product, productDetailsId, ltkId, isRetailerProduct, name, advertiserName } = useProduct(productId);

const { price, discountPrice } = useProductPricing(productId, discountPercent);
const productImageProps = useProductImageProps({ productId });
const productLinkProps = useProductLinkProps(productId);
const { ltk } = useLtk(ltkId);
const { like } = useLike(ltkId);

const isConsumerSearchEnabled = computed(
  () => experimentsStore.variant(EXPERIMENTS_CONSUMER_SEARCH).value === FEATURE_FLAG_ACTIVE,
);
const isSearchPage = computed(() => route.value.name === "search");

const shareUrl = computed(() => {
  if (ltk.value) {
    const url = new URL(ltk.value.shareUrl);
    url.searchParams.set("product", productId.value);
    return url.toString();
  }
  return null;
});

const discountBadgingFlag = computed(() => FLAG_PRICE_DROP_BADGE);

const discountBadgePercent = computed(() => {
  const oneHundred = 100;
  const percentChange = productDetailsStore.productDetails[productDetailsId.value]?.percentChange;
  return Math.round(Number(percentChange) * oneHundred);
});

const likeButtonStyles = computed(() => ({
  "--like-button-offset": props.likeButtonOffset,
  "--like-button-size": props.likeButtonSize,
}));

const productImpressionPayload = computed(() =>
  createProductImpression(Actions.View, {
    product_id: productId.value,
    promotion_id: ltkId.value,
    creator_id: ltk.value?.profileId ?? "",
  }),
);

const searchAnalytics = () => {
  amplitude?.sendExitEvent("/ltk-search-summary", {
    "referring-component": referringComponent.value,
    ...(isConsumerSearchEnabled.value
      ? {
          "post-count": consumerSearchStore.ltks.length,
          "profile-count": consumerSearchStore.profiles.length,
        }
      : {
          "post-count": searchStore.searchTypeItem("post").active.total,
          "profile-count": searchStore.searchTypeItem("profile").active.total,
        }),
    query: route.value.query.keyword.toString(),
  });
  // If consumer search is enabled, do not send search insights feedback to Alogolia
  if (isConsumerSearchEnabled) {
    return;
  }
  // Else, send search insights feedback to Algolia
  let position = -1;
  if (route.value.query.type === "post") {
    // If this is a "post" search, we can pull the index from the LTK store
    position = searchStore.searchTypeItem("post").active.ids.findIndex((id) => id === ltkId.value);
  } else {
    // If this is a "profile" search we can pull the index from the Profile store
    position = searchStore
      .searchResultsProfileIds("profile")
      .findIndex((profileId) => profileId === ltk.value?.profileId);
  }

  searchStore.resultClick({
    type: route.value.query.type as SearchType,
    event: "Search Result Click",
    objectId: ltkId.value,
    position,
    rootReferrer: amplitudeStore.rootReferrer ?? "",
    searchExperience: `Search-${route.value.query.type}`,
  });
};

const onProductClick = () => {
  if (!product.value) {
    return;
  }

  if (isSearchPage.value) {
    searchAnalytics();
  }

  useCreatorShop().setReferringShopTab();

  amplitude?.sendExitEvent("/product-browser", {
    "ltk-id": ltkId.value,
    "ltk-product-id": productId.value,
    "product-id": productDetailsId.value,
    matching: product.value.matching,
    "product-count": ltk.value?.productIds.length,
    index: ltk.value?.productIds.indexOf(productId.value),
    "advertiser-name": advertiserName.value,
    ltkLiked: !!like.value,
    url: productLinkProps.value.href ?? productLinkProps.value.to,
    "branch-journey-creative-name": branch.lastDisplayedBanner,
    "cookie-banner-creative-name": branch.lastDisplayedCookieBanner || "none",
    "referring-component": amplitude?.getReferringComponentByPage(route.value.name ?? "", "product feed"),
    ...(isRetailerProduct.value && { "ltk-direct-platform": "shopify" }),
  });
};

const onRetailerProductClick = (event: MouseEvent, navigate: (event: MouseEvent) => void) => {
  onProductClick();
  navigate(event);
};

return { __sfc: true,props, productId, discountPercent, showDiscount, showProductDetails, showRetailer, showShopNow, shareButton, referringComponent, amplitude, route, store, productsStore, productDetailsStore, branch, searchStore, experimentsStore, amplitudeStore, consumerSearchStore, fetch, product, productDetailsId, ltkId, isRetailerProduct, name, advertiserName, price, discountPrice, productImageProps, productLinkProps, ltk, like, isConsumerSearchEnabled, isSearchPage, shareUrl, discountBadgingFlag, discountBadgePercent, likeButtonStyles, productImpressionPayload, searchAnalytics, onProductClick, onRetailerProductClick }
}

})